/**
 * Serial number tracking — individual unit movement across income, outcome, and invoice documents.
 *
 * Every `*DocId` field has a matching `*DocType` field (the real DOCTYPEID) —
 * use it to build edit-document links (`/edit-document/:docType/:docId`)
 * rather than hardcoding a doctype. `IncomeOrderDocType` varies (2 = Income
 * Order, 10 = Purchase Order linked directly). `OutcomeOrderDocType` varies
 * (4 = Shipment Order, 1 = Invoice when `IsManufacturingConsumption` is 1).
 * `IncomeSpecDocType`/`OutcomeSpecDocType` are always 7, `InvoiceDocType` is
 * always 1.
 */
export interface ProductSerialHistory {
  SerialNumber: string;
  IncomeOrderDocId: number | null;
  IncomeOrderDocType: number | null;
  IncomeOrderDocNumber: string | null;
  IncomeOrderDate: string | null;
  IncomeSpecDocId: number | null;
  IncomeSpecDocType: number | null;
  IncomeSpecDocNumber: string | null;
  IncomeSpecDate: string | null;
  OutcomeOrderDocId: number | null;
  OutcomeOrderDocType: number | null;
  OutcomeOrderDocNumber: string | null;
  OutcomeOrderDate: string | null;
  OutcomeSpecDocId: number | null;
  OutcomeSpecDocType: number | null;
  OutcomeSpecDocNumber: string | null;
  OutcomeSpecDate: string | null;
  InvoiceDocId: number | null;
  InvoiceDocType: number | null;
  InvoiceDocNumber: string | null;
  InvoiceDocDate: string | null;
  IsManufacturingConsumption: 0 | 1;
  SerialStatus: 'IN_STOCK' | 'SHIPPED' | 'SHIPPED_NO_RECORDED_INCOME' | 'UNLINKED';
}

/** One row from the product turnover report — opening stock, movements (income/outcome), closing stock, and document details. */
export class TurnoverRow {

  DOCID: number;
  PRODID: number;
  PRODCODE : string;
  DOCNAME  : string;
  DOCNUMBER  : string;
  DOCDATE: Date;
  DOCNOTE1 : string;
  DOCNOTE2 : string;
  STOCKBE: number;
  INCOME: number;
  OUTCOME: number;
  STOCKAF: number;
  DOCITEMPRICE: number;
  CRATE: number;
  VPRICE: number;
  DEPNAME : string;
  CLINAME : string;
  LOCCOMP : string;
  DOCTYPE: number;
  LCSTOCKBE: number;
  LCSTOCKAF: number;
  DOCMANCLIID: number;
  DOCMANDEPID: number;
  DOCLCOCLIID: number;
  DOCCLIENTSID: number;
  DOCCLIDEPID: number;
  DOCPCOCLIID: number;
  DIRECTION: number;

}

/** Maps a `ProdVendors` DB row — vendor-specific pricing configuration for a product, with formula variables and margin settings. */
export class ProdVendors {

  VENDORSITEMID: number ;
  VENDORID: number ;
  PRODID: number ;
  VENDORNOTE: string ;
  VENDORPRICE: number ;
  VPCURRID: number ;
  VPINCOMPRICEXPR: string;
  EUPRICEEXPR: string;
  VENDORMARGIN: number ;
  VENROUNDBASE: number ;
  VPADDVAR: number ;
  VPADDCONST: number ;
  VPMULVAR: number ;
  VPMULCONST: number;

  VENDORNAME?: string;
  VENDORNIKNAME?: string;
  CURCODE?: string;
  CURNAME?: string;
  CURRATE?: number;
  VAVARCODE?: string;
  VAVARVALUE?: number;
  VAVARNOTE?: string;
  VMVARCODE?: string;
  VMVARVALUE?: number;
  VMVARNOTE?: string;

  VPINCOMEPRICE?: number;
  VPOUTCOMEPRICE?: number;

  outComePriceInMainCurrency?: number;

  VENDORPREVPRICE?: number;
  PRODPREVPRICE?: number;

  vpc?: number;  /* Vendor price change in % */
  ppc?: number;  /* Retail price change in % */

  vpcstr?: string;

  cloneIndex?: number;

  /*
   *
   * SELECT  Cr.CURCODE, Cr.CURNAME,
		Pv.VENDORSITEMID, Pv.VENDORID, Pv.VENDORNOTE, Pv.VENDORPRICE, Pv.VPCURRID, Pv.VPINCOMPRICEXPR, Pv.EUPRICEEXPR,
		Pv.VENDORMARGIN, Pv.VENROUNDBASE, Pv.VPADDVAR, Pv.VPADDCONST, Pv.VPMULVAR, Pv.VPMULCONST, Pv.PRODID,
		RTRIM( Va.VARCODE) AS VAVARCODE, Va.VARVALUE AS VAVARVALUE, Va.VARNOTE AS VAVARNOTE,
		RTRIM( Vm.VARCODE) AS VMVARCODE, Vm.VARVALUE AS VMVARVALUE, Vm.VARNOTE AS VMVARNOTE,
		RTRIM( Vn.CLIENTSNIKNAME) AS VENDORNIKNAME, RTRIM( Vn.CLIENTSNAME) AS VENDORNSAME
FROM
		ProdVendors AS Pv
		INNER JOIN Currencies AS Cr ON Cr.CURID = Pv.VPCURRID
		INNER JOIN Clients AS Vn on Pv.VENDORID = Vn.CLIID
		LEFT  OUTER JOIN Variables AS Va ON Pv.VPADDVAR = Va.VARID
		LEFT OUTER JOIN Variables AS Vm  ON Pv.VPMULVAR = Vm.VARID

WHERE Pv.PRODID=93189
Cr.CURCODE, Cr.CURNAME, ' +
		                    'Pv.VENDORSITEMID, Pv.VENDORID, Pv.VENDORNOTE, Pv.VENDORPRICE, Pv.VPCURRID, Pv.VPINCOMPRICEXPR, Pv.EUPRICEEXPR, ' +
		                    'Pv.VENDORMARGIN, Pv.VENROUNDBASE, Pv.VPADDVAR, Pv.VPADDCONST, Pv.VPMULVAR, Pv.VPMULCONST, Pv.PRODID, ' +
		                    'Va.VARCODE AS VAVARCODE, Va.VARVALUE AS VAVARVALUE, Va.VARNOTE AS VAVARNOTE, ' +
		                    'Vm.VARCODE AS VMVARCODE, Vm.VARVALUE AS VMVARVALUE, Vm.VARNOTE AS VMVARNOTE ' +
*/

}

/** Historical product-price snapshot — income/outcome prices and the associated currency record. */
export class ProdPrice {

  EUPRICEID: number;
  EUPRICEDATE: number;
  EUPRICEVALUE: number;
  INCOMEPRICE: number;
  OUTCOMEPRICE: number;
  CURRID: number;
}

/**
 * Aggregated product info view — combines `Products` table fields with calculated pricing,
 * stock levels, and vendor data.  Fields annotated with `+` for the source API call that
 * populates them (`getProdStockInfoById` / `getProdPriceInfoById`).
 */
export class ProductInfo {
  PRODID: number;
  PRODCODE?: string;
  PRODNAME?: string;
  PRODPRICE?: number;     // - getProdStockInfoById | + getProdPriceInfoById
  PRODBASEDISC?: number;  // - getProdStockInfoById | + getProdPriceInfoById
  PRODQTYDISC?: number;   // - getProdStockInfoById | + getProdPriceInfoById
  PRODDIALDISC?: number;  // - getProdStockInfoById | + getProdPriceInfoById

  BASEDISCPRICE?: number; // - getProdStockInfoById | + getProdPriceInfoById
  QTYDISCPRICE?: number;  // - getProdStockInfoById | + getProdPriceInfoById
  DIALDISCPRICE?: number; // - getProdStockInfoById | + getProdPriceInfoById

  PRODINPRICE?: number;   // - getProdStockInfoById | + getProdPriceInfoById

  PRODSTOCK?: number;     // + getProdStockInfoById | - getProdPriceInfoById  - Overall stock
  PRODSOLD?: number;      // + getProdStockInfoById | - getProdPriceInfoById
  PRODRESERVE?: number;   // + getProdStockInfoById | - getProdPriceInfoById
  PRODONWAY?: number;     // + getProdStockInfoById | - getProdPriceInfoById

  PRODWEIGHT?: number;    // - getProdStockInfoById | + getProdPriceInfoById

  VENDORID?: number;      // + getProdStockInfoById | + getProdPriceInfoById
  VENDORNAME?: string;    // default supplier name. + getProdStockInfoById | + getProdPriceInfoById
  VENDORCURRID?: number;  // + getProdStockInfoById | + getProdPriceInfoById
  VENDORPRICE?: number;   // - getProdStockInfoById | + getProdPriceInfoById
  VENADDCONST?: number;   // - getProdStockInfoById | + getProdPriceInfoById
  VENMULCONST?: number;   // - getProdStockInfoById | + getProdPriceInfoById
  VENADDVAR?: number;     // - getProdStockInfoById | + getProdPriceInfoById
  VENMULVAR?: number;     // - getProdStockInfoById | + getProdPriceInfoById

  //
  //  Below are for advanced economy (profit) calcualtions
  //
  PRODSTOCKM?: number;    // + getProdStockInfoById | - getProdPriceInfoById  - Main stock
  PRODSTOCKI?: number;    // + getProdStockInfoById | - getProdPriceInfoById  - Import stock
  PRODSTOCKA?: number;    // + getProdStockInfoById | - getProdPriceInfoById  - Aux stock (recently used to count another warehouse))
   
  PRODINPRICEM?: number;  // Placeholder only
  PRODINPRICEI?: number;  // Placeholder only
  PRODINPRICEA?: number;  // Placeholder only

  VENDORCURCODE?: string; // Placeholder filled in some routines (extended product info export)
}

/*
 *
 * TODO: Needs to rework field names
 * 
GRAFDATE	GRAPHENDDATE	GRAFVALUE	GRAFVALUE1	GRAFVALUE2	GRAFVALUE3	GRAFVALUE4
2020-07-01 00:00:00.000	2020-08-01 00:00:00.000	0	0	8	7	11
 */

/** Monthly/periodic product sales statistics row — date range and up to five GRAFVALUE series columns. */
export class ProductStat {

  PRODID?: number;
  PRODCODE?: string;
  PRODNAME?: string;
  GRAFDATE: Date;
  GRAPHENDDATE: Date;
  GRAFVALUE:  number;
  GRAFVALUE1: number;
  GRAFVALUE2: number;
  GRAFVALUE3: number;
  GRAFVALUE4: number;
}

/*
[{ "DOCID": 87881,
    "PRODID": 79353,
    "DOCNUMBER": "K21-6708",
    "DOCDATE": "2020-11-19T09:12:40.000Z",
    "CLIENTSNAME": "ТОВ \"ЕНЕРГО ІНЖИНІРИНГ\"",
    "INPROD": "EKI-2525-BE",
    "ORDERED": 10,
    "SHIPPED": 0,
    "DEBTS": 10,
    "MANUED": 0,
    "RESERVEDTILL": "ПРОДАНО",
    "LOCCOMP": "К21ТОВ                          " }]
*/

/** Active booking (reservation) record for a product — links the document, client, and unfulfilled quantities. */
export class ProductBooking {

  DOCID: number;
  PRODID: number;
  DOCNUMBER: string;
  DOCDATE: Date;
  CLIENTSNAME: string;
  INPROD: string;
  ORDERED: number;
  SHIPPED: number;
  DEBTS: number;
  MANUED: number;
  RESERVEDTILL: string;
  LOCCOMP: string;
}

/** Maps a `ProductParts` DB row — one component within a product's bill-of-materials with quantity and display info. */
export class ProductParts {

  PPID: number;
  PRODID: number;
  PARTID: number;
  QUANTITY: number;
  PARTNOTE: string;
  PARTCODE?: string;
  PARTNAME?: string;
  PARTPRICE?: number;
  PARTSTOCK?: number;
  PARTSOLD?: number;
  PARTRESERVE?: number;
  PARTONWAY?: number;
  cloneIndex?: number;
}

/** Maps an `ApprovedCustomsCodes` DB row — a customs tariff code approved for a product with source and approval date. */
export class ApprovedCustomsCodes {

  PRODID: number;       // ProdID reference
  ACTC_DATE: Date;      // Approval date
  ACTC_STRING: string;  // Approved Customs Tariff Code Number String
  ACTC_FLAG: number;    // Control Flag (reserved)
  ACTC_SOURCE: string;  // Approval Source (manual, document, etc)
  PERSID: number;       // Approved by Person
  cloneIndex?: number;  // Service (reserved)
};
/** Full product entity — mirrors the `Products` DB table row with pricing, stock, weight, vendor references, and computed UI state. */
export class Product {

  PRODID: number ;
  PRODVENDORID: number ;
  PRODCODE: string ;
  PRODNAME: string ;
  PRODDESCRIPTION: string ;
  PRODNOTE: string ;
  PRODSALERID: number ;
  PRODARTICUL: string ;
  PRODSTOCK: number ;
  PRODONHAND: number ;        // Use as PRODONTHEWAY (InTransit)
  PRODRESERV: number ;
  PRODGROUP: number ;
  PRODWEIGHT: number ;
  PRODPACK: number ;
  PRODPACKGW: number ;
  PRODPACKVOL: number ;
  PRODPRICEVID: number ;
  PRODCUSTCODE: string ;
  PRODBARCODEMASK: string ;
  PRODWARRANTY: number ;
  PRODBASEDISC: number ;
  PRODQTYDISC: number ;
  PRODDIALDISC: number ;
  PRODDELTERM: number ;
  PRODEUPRICEID: number ;
  PRODKIND: number ;
  PRODOBSOLETE: number ;
  PRODWWWPAGE: string ;
  PRODCHECKMETHOD: string ;
  PRODVAT: number;

  PRODSOLD?: number;

  _PRODPARTS?: ProductParts[];
  _PRODVENDORS?: ProdVendors[];
  _PRODPRICE?: ProdPrice;

  _PRODUCTINFO?: ProductInfo;

  PRODGROUPNAME?: string;
  PRODVENDORNAME?: string;

  PRODPRICEUAH?: number;
  PRODPRICEUSD?: number;

  CURRENCYRATE?: number;      // To temporary pass the currency rate during calculations;

  PRODPRICEUAH2?: number;     // To compare competitors price
  PRODPRICEUSD2?: number;

  PRODCATEGORY?: string;   // Product Global Category

  ACTC_LIST?: ApprovedCustomsCodes[];

  changed?: boolean;
};


/**
 * Enrichment data returned by POST /Product/BatchInfo for a single product.
 * One record per PRODID in the request; results are unordered — index by PRODID.
 */
export interface ProductBatchInfo {
  PRODID:         number;
  PRODSTOCK:      number;
  PRODPRICEUAH:   number;
  PRODPRICEUSD:   number;
  PRODBASEDISC:   number;   // min discount tier (from Products table)
  PRODQTYDISC:    number;   // qty discount tier (from Products table)
  PRODDIALDISC:   number;   // max discount tier (from Products table)
  PRODVENDORNAME: string;
  PRODGROUPNAME:  string;
  VENDORCURRID:   number;
  VENDORCURCODE:  string;
}

/**
 * Parameters for fetching a list of products from the API.
 *
 * Used to control pagination, filtering, sorting, and currency settings
 * when retrieving product lists.
 */
export class ProductListFetchParams {
  /** Main currency ID to express the product price */
  mc?: number;
  /** Balance currency ID to express the product price */
  bc?: number;
  /** Currency date for exchange rate lookup */
  fd?: Date;
  /** Start from record (offset for pagination) */
  st?: number;
  /** Number of records to fetch (limit for pagination) */
  ft?: number;
  /** Filter parameters string */
  fs?: any;
  /**
   * Sorting parameters object.
   * @property sf - Sort field name
   * @property so - Sort order (e.g. 1 for ascending, -1 for descending)
   */
  sp?: {
    sf: string;
    so: number;
  };

  /** Optional stock depot ID #1 for AllStockDepots calls */
  stock1?: number;
  /** Optional stock depot ID #2 for AllStockDepots calls */
  stock2?: number;
  /** Optional stock depot ID #3 for AllStockDepots calls */
  stock3?: number;
}
